import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Badge
} from '@mui/material';
import {
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from 'notistack';
import api from '../api/api';
import PageHeader from '../components/PageHeader';

interface SchoolApplication {
  id: number;
  school_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
  message?: string;
  status: string;
  created_at: string;
  reviewed_at?: string;
  reviewed_by?: number;
}

interface ApplicationStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

const SchoolApplications: React.FC = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  
  // State
  const [applications, setApplications] = useState<SchoolApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ApplicationStats>({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [selectedApplication, setSelectedApplication] = useState<SchoolApplication | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // Filters and pagination
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Review form
  const [reviewStatus, setReviewStatus] = useState<'approved' | 'rejected' | ''>('');
  const [reviewMessage, setReviewMessage] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  useEffect(() => {
    if (user?.role === 'superuser') {
      fetchApplications();
      fetchStats();
    }
  }, [user, statusFilter, page, rowsPerPage]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      params.append('skip', (page * rowsPerPage).toString());
      params.append('limit', rowsPerPage.toString());
      
      const response = await api.get(`/schools/applications?${params}`);
      setApplications(response.data as any[]);
    } catch (error: any) {
      enqueueSnackbar('Failed to fetch applications', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const [allRes, pendingRes, approvedRes, rejectedRes] = await Promise.all([
        api.get('/schools/applications'),
        api.get('/schools/applications?status=pending'),
        api.get('/schools/applications?status=approved'),
        api.get('/schools/applications?status=rejected')
      ]);
      
      setStats({
        total: (allRes.data as any[]).length,
        pending: (pendingRes.data as any[]).length,
        approved: (approvedRes.data as any[]).length,
        rejected: (rejectedRes.data as any[]).length
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleReview = (application: SchoolApplication) => {
    setSelectedApplication(application);
    setReviewStatus('');
    setReviewMessage('');
    setReviewDialogOpen(true);
  };

  const handleView = (application: SchoolApplication) => {
    setSelectedApplication(application);
    setViewDialogOpen(true);
  };

  const handleReviewSubmit = async () => {
    if (!selectedApplication || !reviewStatus) return;
    
    setReviewSubmitting(true);
    try {
      await api.post(`/schools/applications/${selectedApplication.id}/review`, {
        status: reviewStatus,
        message: reviewMessage,
      });
      
      enqueueSnackbar(`Application ${reviewStatus === 'approved' ? 'approved' : 'rejected'} successfully`, { variant: 'success' });
      setReviewDialogOpen(false);
      fetchApplications();
      fetchStats();
    } catch (error: any) {
      enqueueSnackbar('Failed to submit review', { variant: 'error' });
    } finally {
      setReviewSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredApplications = applications.filter(app =>
    app.school_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.contact_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (user?.role !== 'superuser') {
    return <Alert severity="error">You are not authorized to view this page.</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="School Applications"
        subtitle="Manage and review school applications"
        action={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchApplications(); fetchStats(); }}
          >
            Refresh
          </Button>
        }
      />

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Applications</Typography>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Pending Review</Typography>
              <Typography variant="h4" color="warning.main">{stats.pending}</Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Approved</Typography>
              <Typography variant="h4" color="success.main">{stats.approved}</Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Rejected</Typography>
              <Typography variant="h4" color="error.main">{stats.rejected}</Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
            <TextField
              fullWidth
              placeholder="Search applications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Box>
          <Box sx={{ flex: '0 1 200px', minWidth: '200px' }}>
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status Filter"
              >
                <MenuItem value="all">All Applications</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Applications Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>School Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Applied</TableCell>
                <TableCell>Reviewed</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredApplications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="textSecondary">No applications found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredApplications.map((app) => (
                  <TableRow key={app.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="medium">
                        {app.school_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{app.contact_name}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {app.contact_email}
                        </Typography>
                        {app.contact_phone && (
                          <Typography variant="body2" color="textSecondary">
                            {app.contact_phone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={app.status}
                        color={getStatusColor(app.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(app.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {app.reviewed_at ? (
                        <Typography variant="body2">
                          {formatDate(app.reviewed_at)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Not reviewed
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleView(app)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {app.status === 'pending' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => handleReview(app)}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleReview(app)}
                            >
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredApplications.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* View Application Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon />
            Application Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Typography variant="h6" gutterBottom>School Information</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">School Name</Typography>
                    <Typography variant="body1">{selectedApplication.school_name}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Application ID</Typography>
                    <Typography variant="body1">#{selectedApplication.id}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                    <Chip
                      label={selectedApplication.status}
                      color={getStatusColor(selectedApplication.status) as any}
                      size="small"
                    />
                  </Box>
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Typography variant="h6" gutterBottom>Contact Information</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Contact Name</Typography>
                    <Typography variant="body1">{selectedApplication.contact_name}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                    <Typography variant="body1">{selectedApplication.contact_email}</Typography>
                  </Box>
                  {selectedApplication.contact_phone && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                      <Typography variant="body1">{selectedApplication.contact_phone}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box sx={{ width: '100%', mt: 3 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Timeline</Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">Applied</Typography>
                  <Typography variant="body1">{formatDate(selectedApplication.created_at)}</Typography>
                </Box>
                {selectedApplication.reviewed_at && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Reviewed</Typography>
                    <Typography variant="body1">{formatDate(selectedApplication.reviewed_at)}</Typography>
                  </Box>
                )}
              </Box>
              {selectedApplication.message && (
                <Box sx={{ width: '100%', mt: 3 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Message</Typography>
                  <Typography variant="body1">{selectedApplication.message}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedApplication?.status === 'pending' && (
            <Button 
              variant="contained" 
              onClick={() => {
                setViewDialogOpen(false);
                handleReview(selectedApplication);
              }}
            >
              Review Application
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Review Application</DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>School:</strong> {selectedApplication.school_name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Contact:</strong> {selectedApplication.contact_name} ({selectedApplication.contact_email})
              </Typography>
              
              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel>Decision</InputLabel>
                <Select
                  value={reviewStatus}
                  onChange={(e) => setReviewStatus(e.target.value as 'approved' | 'rejected')}
                  label="Decision"
                  disabled={reviewSubmitting}
                >
                  <MenuItem value="approved">Approve</MenuItem>
                  <MenuItem value="rejected">Reject</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                label="Reviewer Comment (optional)"
                fullWidth
                multiline
                minRows={3}
                sx={{ mt: 2 }}
                value={reviewMessage}
                onChange={(e) => setReviewMessage(e.target.value)}
                disabled={reviewSubmitting}
                placeholder="Add any additional comments or feedback..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)} disabled={reviewSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleReviewSubmit}
            variant="contained"
            color={reviewStatus === 'approved' ? 'success' : 'error'}
            disabled={!reviewStatus || reviewSubmitting}
            startIcon={reviewSubmitting ? <CircularProgress size={16} /> : undefined}
          >
            {reviewSubmitting ? 'Submitting...' : reviewStatus === 'approved' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolApplications; 